/**
 * Component in lib folder because it should belong to the design system
 * Temporary local component until we have a version on design system
 */

import {
  useState,
  useEffect,
  ReactNode,
  useMemo,
  forwardRef,
  Ref,
  useImperativeHandle,
} from 'react';
import { Button, Tooltip } from '@ets/design-system';
import cn from 'classnames';

import { ProgressBarGroup } from './ProgressBarGroup';

import { Pulse, type PulseProps } from './Pulse';
import { CutoutBackdrop, Cutout } from './CutoutBackdrop';

import styles from './styles.module.scss';

export type OnBoardingCarBoxProps = {
  top?: string;
  left?: string;
  bottom?: string;
  right?: string;
  width?: string;
  height?: string;
};

type BoxProps = {
  box?: OnBoardingCarBoxProps;
  testId: string;
};

type CardPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

export type OnboardingHandler = {
  next: () => void;
};

type Card = {
  /** Specify the title of the card  */
  title?: ReactNode;
  /** Specify the description of the card/phase  */
  description: ReactNode;
  /** Specify the tip description of the step */
  tipValue?: ReactNode;
  /** Specify the value of the progress */
  progress: number;
  /** Specify the value of the current phase */
  currentPhase: number;
  /** Specify if the part of the phases should appear on the card */
  showPhases?: boolean;
  /** Specify the label of the next button */
  nextLabel?: ReactNode;
  /** Specify the number of the phase that was completed */
  phaseCompleted?: number;
  /** Specify the number of the sub phase */
  subPhase?: number;
  /** Specify a non default title of the phase */
  phaseTitle?: string;
  pulse?: Omit<PulseProps, 'testId'>;
  box?: OnBoardingCarBoxProps;
  visibleSpace?: Cutout[];
  position?: CardPosition;
};

export type OnboardingCardsProps = {
  pulse?: ReactNode;
  box?: ReactNode;
  /** Back button label */
  previousLabel: ReactNode;
  /** Next button label */
  next: ReactNode;
  /** An object of objects that contains the phases, the label of the phase has and their order */
  phases: { [key: string]: { order: number; label: ReactNode } };
  /** Specify the tip label */
  tip?: ReactNode;
  /** Function that indicates the index of the current card */
  onNext?: (activeCard: number) => void;
  /** Function that indicates the index of the current card */
  onBack?: (activeCard: number) => void;
  /** Specify the title of the card  */
  title: ReactNode;
  /** Specify if the next button is disabled */
  nextDisabled?: boolean;
  /** Specify next button tooltip if the button is disabled */
  nextDisabledTooltip?: string;
  /** Specify the offset of the card */
  offset?: {
    top?: string;
    right?: string;
    bottom?: string;
    left?: string;
  };
  /** Specifiy the number of the current card */
  initialCard?: number;
  /** An array of objects that contains the cards */
  cards: Card[];
  /** Handler to be called when the user close the modal */
  onCancel?: () => void;
  onFinalExit: () => void;
  /** Function that will be called to initiate the exit transition. */
  onExit?: () => void;
  /** Boolean to set if the modal is open or not */
  open?: boolean;
  skipButtonLabel: ReactNode;
  skipPreviousLabel: ReactNode;
  skipFinishLabel: ReactNode;
  skipHeader: ReactNode;
  skipContent: ReactNode;
  width?: CSSStyleDeclaration['width'];
  size: 's' | 'm';
  /** Indicates if we want to skip the onboarding */
  skip?: boolean;
  /** Handler to be called when we want a different action on the cancel skip button */
  cancelSkip?: () => void;
  testId: string;
};

type HeaderProps = {
  skip?: ReactNode;
  skipOnboarding?: () => void;
  phaseAmount: number;
  skipButtonLabel?: ReactNode;
  progressValue?: number;
  showSkipOnboardingButton?: boolean;
  lastCard: boolean;
  title: ReactNode;
  card: Card;
  testId: string;
};

type BodyProps = {
  card: Card;
  phases: {
    [key: string]: {
      order: number;
      label: ReactNode;
    };
  };
  skip?: ReactNode;
  tip?: ReactNode;
};

export const Box = ({ box, testId }: BoxProps) => {
  return (
    <div
      className={styles.box}
      style={{
        top: box?.top,
        left: box?.left,
        right: box?.right,
        bottom: box?.bottom,
        height: box?.height,
        width: box?.width,
      }}
      data-testid={`onboarding-${testId}-box`}
    />
  );
};

const Middle = ({ card, phases, tip }: BodyProps) => {
  const showPhases = card.showPhases ?? true; //Undefined is true
  const phase = Object.entries(phases).filter(([_, values]) => values.order === card.currentPhase);
  const handleNumbering = () => {
    if (card && card.currentPhase > 0) {
      const phaseTitle = card.phaseTitle ?? phase[0][1].label;

      if (card.subPhase) {
        return `${card.currentPhase}.${card.subPhase}. ${phaseTitle}`;
      } else {
        return `${card.currentPhase}. ${phaseTitle}`;
      }
    } else {
      return '';
    }
  };

  return (
    <>
      {handleNumbering() && <div className={styles.title}>{handleNumbering()}</div>}
      <div className={styles.description}>{card.description}</div>
      {card.tipValue && (
        <div className={styles.tip}>
          {tip}
          {` `}
          <div className={styles.tipText}>{card.tipValue}</div>
        </div>
      )}
      {showPhases && <Steps phases={phases} card={card} />}
    </>
  );
};

const Card = ({ card, skip, phases, tip }: BodyProps) => {
  return (
    <>
      {skip ? (
        <div className={styles.skipInfo}>{skip}</div>
      ) : (
        <Middle card={card} phases={phases} tip={tip} />
      )}
    </>
  );
};

const Steps = ({ phases, card }: BodyProps) => {
  return (
    <div className={styles.stepContainer}>
      {Object.entries(phases).map(([_, phaseValues]) => {
        const active =
          card?.currentPhase === phaseValues.order && phaseValues.order !== card.phaseCompleted;
        const disabled =
          phaseValues.order < card?.currentPhase || phaseValues.order === card.phaseCompleted;
        return (
          <div key={phaseValues.order} className={cn(styles.step, { [styles.disabled]: disabled })}>
            <div className={cn(styles.stepNumber, { [styles.active]: active })}>
              <div
                className={cn(styles.stepNumberText, {
                  [styles.active]: active,
                })}
              >
                {phaseValues?.order}
              </div>
            </div>
            <div className={styles.stepLabel}>{phaseValues.label}</div>
          </div>
        );
      })}
    </div>
  );
};

const Header = ({
  skip,
  skipOnboarding,
  phaseAmount,
  skipButtonLabel,
  lastCard,
  card,
  title,
  testId,
}: HeaderProps) => {
  return (
    <>
      {skip ? (
        <div className={styles.skipTitle}>{skip}</div>
      ) : (
        <div
          className={cn(styles.header, {
            [styles.lastCard]: lastCard,
          })}
        >
          <div className={cn(styles.headerTitleBar, { [styles.lastCard]: lastCard })}>
            <div className={styles.titleBarText}>{card.title ?? title}</div>
            {!lastCard && (
              <Button
                size="m"
                variant="link"
                onClick={skipOnboarding}
                testId={`onboarding-${testId}-skip`}
              >
                {skipButtonLabel}
              </Button>
            )}
          </div>
          {!lastCard && (
            <ProgressBarGroup
              size="m"
              variant="primary-light"
              progress={card.progress}
              testId="onboarding"
              steps={phaseAmount}
              activeStep={card.currentPhase}
            />
          )}
        </div>
      )}
    </>
  );
};

export const OnboardingCards = forwardRef(
  (
    {
      cards = [],
      previousLabel,
      next,
      onNext,
      onBack,
      onExit,
      onFinalExit,
      open = true,
      skipPreviousLabel,
      skipFinishLabel,
      skipHeader,
      skipContent,
      width,
      size,
      phases,
      skipButtonLabel,
      tip,
      pulse,
      box,
      title,
      nextDisabled,
      skip = false,
      cancelSkip,
      nextDisabledTooltip,
      offset,
      initialCard,
      testId,
    }: OnboardingCardsProps,
    ref: Ref<OnboardingHandler>,
  ) => {
    const [currentCardIndex, setCurrentCardIndex] = useState(0);
    const [skipState, setSkipState] = useState(false);

    const currentCard = useMemo(() => cards[currentCardIndex], [cards, currentCardIndex]);

    useImperativeHandle(ref, () => ({
      next() {
        nextCard();
      },
    }));

    useEffect(() => {
      if (initialCard) {
        setCurrentCardIndex(initialCard);
      }
    }, [initialCard]);

    const handleKeydown = (e: KeyboardEvent) => {
      if (document?.activeElement?.hasAttribute('contenteditable')) return;

      if (e.key === 'ArrowRight') {
        nextCard();
      }
      if (e.key === 'ArrowLeft') {
        previousCard();
      }
    };

    useEffect(() => {
      window.addEventListener('keydown', handleKeydown);
      return () => {
        window.removeEventListener('keydown', handleKeydown);
      };
    });

    const previousCard = () => {
      if (!skip && !skipState) {
        if (currentCardIndex === 0) {
          onBack?.(0);
        }
        if (currentCardIndex > 0) {
          const value = currentCardIndex - 1;
          onBack?.(value);
          setCurrentCardIndex(value);
        }
      }
    };

    const nextCard = () => {
      if (!nextDisabled && !skip && !skipState) {
        if (currentCardIndex === cards.length - 1) {
          onFinalExit();
        } else {
          const value = currentCardIndex + 1;
          onNext?.(value);
          setCurrentCardIndex(value);
        }
      }
    };

    const onCancel = () => {
      setSkipState(!skipState);
    };

    const cardPosition = useMemo(() => {
      const position: CardPosition = cards[currentCardIndex].position ?? 'bottom-right';
      const positionOffset = {
        top: offset?.top ?? '48px',
        right: offset?.right ?? '48px',
        bottom: offset?.bottom ?? '48px',
        left: offset?.left ?? '48px',
      };

      switch (position) {
        case 'top-left': {
          return {
            top: positionOffset.top,
            left: positionOffset.left,
          };
        }
        case 'top-right': {
          return {
            top: positionOffset.top,
            right: positionOffset.right,
          };
        }
        case 'bottom-left': {
          return {
            bottom: positionOffset.bottom,
            left: positionOffset.left,
          };
        }
        case 'bottom-right':
        default: {
          return {
            bottom: positionOffset.bottom,
            right: positionOffset.right,
          };
        }
      }
    }, [cards, currentCardIndex, offset]);

    if (!open) {
      return null;
    }

    return (
      <>
        <CutoutBackdrop cutouts={currentCard.visibleSpace} testId={testId} />
        <div
          className={styles.container}
          data-slot="onboarding-container"
          style={{
            width: width || '51rem',
            ...cardPosition,
            position: 'fixed',
          }}
          data-testid={`onboarding-${testId}-card-${currentCardIndex}`}
        >
          <Header
            skip={skip || skipState ? skipHeader : undefined}
            skipOnboarding={onCancel}
            skipButtonLabel={skipButtonLabel}
            phaseAmount={Object.keys(phases).length}
            progressValue={currentCard.progress}
            lastCard={currentCardIndex === cards.length - 1}
            card={currentCard}
            title={title}
            testId={testId}
          />
          <div className={styles.body}>
            <Card
              skip={skip || skipState ? skipContent : undefined}
              card={currentCard}
              phases={phases}
              tip={tip}
            />
          </div>
          <div className={styles.footer} style={{ justifyContent: 'space-between' }}>
            {skip || skipState ? (
              <div className={styles.skipButtonContainer}>
                <Button
                  variant="link"
                  size={size}
                  onClick={cancelSkip ?? onCancel}
                  testId={`onboarding-${testId}-skip-back`}
                >
                  {skipPreviousLabel}
                </Button>
              </div>
            ) : (
              <Button
                variant="link"
                size={size}
                onClick={previousCard}
                testId={`onboarding-${testId}-back`}
              >
                {previousLabel}
              </Button>
            )}
            {skip || skipState ? (
              <Button
                variant="danger"
                size={size}
                onClick={onExit}
                testId={`onboarding-${testId}-skip-exit`}
              >
                {skipFinishLabel}
              </Button>
            ) : (
              <Tooltip
                content={nextDisabledTooltip ?? ''}
                visible={nextDisabled}
                testId={`onboarding-${testId}-next-button`}
              >
                <Button
                  variant="primary"
                  size={size}
                  onClick={nextCard}
                  disabled={nextDisabled}
                  testId={`onboarding-${testId}-next`}
                >
                  {currentCard.nextLabel ?? next}
                </Button>
              </Tooltip>
            )}
          </div>
        </div>
        {pulse}
        {box}
        {open && currentCard.pulse && !skip && !skipState && (
          <Pulse {...currentCard.pulse} testId={testId} />
        )}
        {open && currentCard.box && !skip && !skipState && (
          <Box box={currentCard.box} testId={testId} />
        )}
      </>
    );
  },
);
